import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Advertise from "@/pages/Advertise";
import XPulse from "@/pages/XPulse";
import { api } from "@/lib/api";
import { getVisitorId } from "@/lib/ads";
import { trackPage } from "@/lib/analytics";
import { Toaster } from "sonner";
import "@/App.css";
import { I18nProvider } from "@/i18n";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BreakingBar } from "@/components/BreakingBar";
import { LiveNotifier } from "@/components/LiveNotifier";
import { CookieConsent } from "@/components/CookieConsent";
import Home from "@/pages/Home";
import Latest from "@/pages/Latest";
import CategoryPage from "@/pages/CategoryPage";
import ArticlePage from "@/pages/ArticlePage";
import SearchPage from "@/pages/SearchPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { About, HowItWorksPage, TermsOfUse, EditorialPolicy, CopyrightPolicy, AITransparency, PrivacyPolicy, Contact } from "@/pages/StaticPages";

function PublicLayout({ children }) {
  return (
    <>
      <BreakingBar />
      <Header />
      <main className="pt-9">{children}</main>
      <Footer />
      <LiveNotifier />
      <CookieConsent />
    </>
  );
}

function useVisitTracking(pathname) {
  React.useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    trackPage(pathname + window.location.search);
    api.post("/track", { vid: getVisitorId(), path: pathname }).catch(() => {});
  }, [pathname]);
}

function AppRoutes() {
  const location = useLocation();
  useVisitTracking(location.pathname);
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    );
  }

  return (
    <PublicLayout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/latest" element={<Latest />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/article/:id" element={<ArticlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/editorial-policy" element={<EditorialPolicy />} />
        <Route path="/copyright-policy" element={<CopyrightPolicy />} />
        <Route path="/ai-transparency" element={<AITransparency />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/x-pulse" element={<XPulse />} />
      </Routes>
    </PublicLayout>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="bottom-right" richColors />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
